import { useSnackbar } from "notistack";
import type { Query } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UploadCsvResponse } from "./productPricesImportApi";
import { uploadCsvWithPrices } from "./productPricesImportApi";
import type { PriceType } from "../priceType";

/*
  it matches patterns like below
  - ["products", {productId}, "prices", "selling", {date}]
  - ["products", {productId}, "options", {oId}, "prices"]
 */
function isPriceQuery(priceType: PriceType) {
  return ({ queryKey }: Query) =>
    Array.isArray(queryKey) &&
    queryKey.length === 5 &&
    queryKey[0] === "products" &&
    (queryKey.at(-1) === "prices" || queryKey.at(-2) === priceType);
}

export const usePriceCsv = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const uploadPriceCsvMutation = useMutation<UploadCsvResponse, unknown, { data: FormData; priceType: PriceType }>({
    mutationFn: ({ data, priceType }) => uploadCsvWithPrices(priceType, data),
    onSuccess: async (r, { priceType }) => {
      enqueueSnackbar(`Ceny ${priceType} dla ${r.optionCount} opcji zostały zaimportowane`, { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isPriceQuery(priceType) });
    },
    onError: (error, { priceType }) => {
      enqueueSnackbar(`Nie udało się zaimportować cen ${priceType}. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    uploadPriceCsv: (priceType: PriceType, data: FormData) => uploadPriceCsvMutation.mutateAsync({ data, priceType }),
    isUploadInProgress: uploadPriceCsvMutation.isPending,
  };
};
